import type { PropertyMembership, User } from '@cht/shared'

/**
 * The signed-in account plus the properties it may act on. A user with no
 * memberships is signed in but has nothing to see — the UI says so rather than
 * failing.
 */
export interface SessionUser extends User {
  properties: PropertyMembership[]
}

export interface AuthTransport {
  fetch(input: string, init?: RequestInit): Promise<Response>
}

const browserTransport: AuthTransport = { fetch: (input, init) => fetch(input, init) }

export class AuthClient {
  constructor(private readonly transport: AuthTransport = browserTransport) {}

  async currentUser(): Promise<SessionUser | undefined> {
    const response = await this.transport.fetch('/api/auth/me', { credentials: 'same-origin' })
    if (response.status === 401) return undefined
    if (!response.ok) throw new Error('Unable to check sign-in status')
    const user = await response.json() as SessionUser
    return { ...user, properties: user.properties ?? [] }
  }

  async requestLink(email: string): Promise<void> {
    const response = await this.transport.fetch('/api/auth/request-link', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ email: email.trim().toLowerCase() })
    })
    if (!response.ok) throw new Error('Unable to send the sign-in link')
  }

  async logout(): Promise<void> {
    const response = await this.transport.fetch('/api/auth/logout', { method: 'POST', credentials: 'same-origin' })
    if (!response.ok) throw new Error('Unable to sign out')
  }
}

export const authClient = new AuthClient()
